import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface CartItem {
  /** PK|SK composite key for the recording */
  recordingId: string;
  /** Display fields for the cart view */
  agentId: string;
  callerId: string;
  recordDate: string;
  systemId: string;
  duration?: string;
  hasTranscript?: boolean;
  originalFilename?: string;
}

const CART_KEY = 'pulse-storage-download-cart';
const MAX_ITEMS = 20;

function loadCart(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

interface DownloadCartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => boolean;
  addItems: (items: CartItem[]) => number;
  removeItem: (recordingId: string) => void;
  clearCart: () => void;
  isInCart: (recordingId: string) => boolean;
  isFull: boolean;
}

const DownloadCartContext = createContext<DownloadCartContextType>({
  items: [],
  addItem: () => false,
  addItems: () => 0,
  removeItem: () => {},
  clearCart: () => {},
  isInCart: () => false,
  isFull: false,
});

export function useDownloadCart() {
  return useContext(DownloadCartContext);
}

export function DownloadCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((item: CartItem) => {
    let added = false;
    setItems(prev => {
      if (prev.length >= MAX_ITEMS) return prev;
      if (prev.some(i => i.recordingId === item.recordingId)) return prev;
      added = true;
      return [...prev, item];
    });
    return added;
  }, []);

  const addItems = useCallback((newItems: CartItem[]) => {
    let count = 0;
    setItems(prev => {
      const existing = new Set(prev.map(i => i.recordingId));
      const toAdd = newItems.filter(i => !existing.has(i.recordingId));
      const space = MAX_ITEMS - prev.length;
      const batch = toAdd.slice(0, space);
      count = batch.length;
      return batch.length > 0 ? [...prev, ...batch] : prev;
    });
    return count;
  }, []);

  const removeItem = useCallback((recordingId: string) => {
    setItems(prev => prev.filter(i => i.recordingId !== recordingId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback((recordingId: string) => {
    return items.some(i => i.recordingId === recordingId);
  }, [items]);

  const value: DownloadCartContextType = {
    items,
    addItem,
    addItems,
    removeItem,
    clearCart,
    isInCart,
    isFull: items.length >= MAX_ITEMS,
  };

  return React.createElement(DownloadCartContext.Provider, { value }, children);
}
